import React, { useCallback } from 'react';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { GOOGLE_CONFIG } from '../../config/env';
import { useAuth } from '../../hooks/useAuth';
import { parseGoogleGetStarted, stashGoogleGetStarted } from '../../utils/googleGetStarted';

interface GoogleSignInButtonProps {
  mode?: 'login' | 'signup';
  disabled?: boolean;
  onAuthStart?: () => void;
  onAuthEnd?: () => void;
  /**
   * When provided, the raw Google credential is handed up INSTEAD of being
   * exchanged for a session — no loginWithGoogle, no toast, no navigation.
   * The sign-up wizard needs the credential to survive steps 2-3 and posts it
   * together with the organization + legal payload at the end.
   */
  onCredential?: (credential: string) => void;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  mode = 'login',
  disabled = false,
  onAuthStart,
  onAuthEnd,
  onCredential,
}) => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuth();
  const clientId = GOOGLE_CONFIG.clientId;

  const handleSuccess = useCallback(
    async (credentialResponse: CredentialResponse) => {
      if (!credentialResponse.credential) {
        toast.error('Google did not return a credential. Please try again.');
        return;
      }
      // Caller owns the credential: it is not a login, so nothing here should
      // create a session, toast, or navigate.
      if (onCredential) {
        onCredential(credentialResponse.credential);
        return;
      }
      onAuthStart?.();
      try {
        // loginWithGoogle populates AuthContext (perms included) before we route;
        // multi-workspace members pick one first, everyone else hits the smart
        // home which lands them in their resolved view mode.
        const { requiresSelection } = await loginWithGoogle(credentialResponse.credential);
        navigate(requiresSelection ? '/auth/select-workspace' : '/');
      } catch (err: any) {
        // A Google account with no workspace is a sign-up, not a failure: carry
        // the credential into the wizard, which asks for the organization.
        const profile = parseGoogleGetStarted(err);
        if (profile) {
          stashGoogleGetStarted({ credential: credentialResponse.credential, profile });
          navigate('/auth/signup');
          return;
        }
        const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
        toast.error(apiMessage || 'Could not sign in with Google. Please try again.');
      } finally {
        onAuthEnd?.();
      }
    },
    [navigate, loginWithGoogle, onAuthStart, onAuthEnd, onCredential]
  );

  const handleError = useCallback(() => {
    toast.error('Google sign-in was canceled or failed. Please try again.');
  }, []);

  if (!clientId) {
    return (
      <div className="text-xs text-text-tertiary text-center">
        Google sign-in is not configured.
      </div>
    );
  }

  return (
    <div
      className="flex justify-center w-full"
      style={{ pointerEvents: disabled ? 'none' : 'auto', opacity: disabled ? 0.5 : 1 }}
      aria-label={mode === 'signup' ? 'Sign up with Google' : 'Sign in with Google'}
    >
      <GoogleLogin
        onSuccess={handleSuccess}
        onError={handleError}
        theme="outline"
        shape="rectangular"
        size="large"
        text={mode === 'signup' ? 'signup_with' : 'signin_with'}
        width="320"
        useOneTap={false}
      />
    </div>
  );
};

export default GoogleSignInButton;
